import { useEffect, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { UserCheck, LogIn, LogOut, Clock, Search, Users as UsersIcon, FileSpreadsheet } from "lucide-react";

const ftime = (s) => s ? new Date(s).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—";
const fdate = (s) => s ? new Date(s + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : s;
const today = () => {
  const d = new Date(Date.now() + (330 + new Date().getTimezoneOffset()) * 60000);
  return d.toISOString().slice(0, 10);
};

const STATUS_LABEL = { checked_in: "Checked-In", present: "Present", absent: "Absent" };
const STATUS_STYLE = {
  checked_in: "bg-emerald-500/10 text-emerald-600",
  present: "bg-blue-500/10 text-blue-600",
  absent: "bg-rose-500/10 text-rose-600",
};

export default function Attendance() {
  const { user } = useAuth();
  const isAdmin = ["super_admin", "company_admin"].includes(user?.role);
  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
      <header className="mb-6">
        <p className="label-eyebrow text-primary">HR</p>
        <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight flex items-center gap-2"><UserCheck className="h-7 w-7" /> Attendance</h1>
        <p className="mt-1 text-sm text-muted-foreground">Check in and out, view your hours — admins see the live team board.</p>
      </header>
      <MyAttendance />
      {isAdmin && <AdminBoard />}
    </div>
  );
}

export function MyAttendance({ compact = false }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/attendance/me").then((r) => setData(r.data)).catch((e) => setErr(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const act = async (path) => {
    setBusy(true); setErr("");
    try { await api.post(`/attendance/${path}`); await load(); }
    catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  if (!data) return null;
  return (
    <section className="sharp-card mb-6 p-5">
      <div className="flex flex-wrap items-center gap-4">
        {data.checked_in ? (
          <button data-testid="attendance-checkout-btn" disabled={busy} onClick={() => act("check-out")}
            className="inline-flex items-center gap-2 rounded-md bg-rose-600 px-5 py-3 text-sm font-bold text-white hover:bg-rose-700 disabled:opacity-60">
            <LogOut className="h-4 w-4" /> {busy ? "…" : "Check Out"}
          </button>
        ) : (
          <button data-testid="attendance-checkin-btn" disabled={busy} onClick={() => act("check-in")}
            className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-5 py-3 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-60">
            <LogIn className="h-4 w-4" /> {busy ? "…" : "Check In"}
          </button>
        )}
        <span data-testid="attendance-my-status" className={`rounded-md px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${data.checked_in ? STATUS_STYLE.checked_in : "bg-slate-500/10 text-slate-500"}`}>
          {data.checked_in ? "Checked-In" : "Checked-Out"}
        </span>
        <span data-testid="attendance-hours-today" className="inline-flex items-center gap-1.5 rounded-md border border-border/60 px-3 py-2 text-xs font-semibold">
          <Clock className="h-3.5 w-3.5 text-primary" /> Today: {data.hours_today} h
        </span>
        <span data-testid="attendance-hours-week" className="inline-flex items-center gap-1.5 rounded-md border border-border/60 px-3 py-2 text-xs font-semibold">
          <Clock className="h-3.5 w-3.5 text-primary" /> This week: {data.hours_week} h
        </span>
      </div>
      {err && <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
      {!compact && (
        <div className="mt-5">
          <p className="label-eyebrow mb-2">My history (last 30 days)</p>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-secondary/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr><th className="px-3 py-2">Date</th><th className="px-3 py-2">Check-In</th><th className="px-3 py-2">Check-Out</th><th className="px-3 py-2">Hours</th></tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {data.sessions.map((s) => (
                  <tr key={s.id} data-testid={`attendance-session-${s.id}`}>
                    <td className="px-3 py-1.5 text-xs">{fdate(s.date)}</td>
                    <td className="px-3 py-1.5 text-xs">{ftime(s.check_in)}</td>
                    <td className="px-3 py-1.5 text-xs">{s.check_out ? ftime(s.check_out) : <span className="text-emerald-600 font-semibold">working…</span>}</td>
                    <td className="px-3 py-1.5 text-xs font-semibold">{s.hours} h</td>
                  </tr>
                ))}
                {data.sessions.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-xs text-muted-foreground">No attendance records yet — hit Check In to start.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

function AdminBoard() {
  const [date, setDate] = useState(today());
  const [q, setQ] = useState("");
  const [board, setBoard] = useState(null);
  const [err, setErr] = useState("");
  const [month, setMonth] = useState(today().slice(0, 7));
  const [exporting, setExporting] = useState(false);

  const exportMonth = async () => {
    setExporting(true); setErr("");
    try {
      const r = await api.get(`/attendance/export.xlsx?month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `attendance-${month}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setErr(formatApiError(e)); } finally { setExporting(false); }
  };

  const load = () => {
    const p = new URLSearchParams({ date });
    if (q) p.set("q", q);
    api.get(`/attendance/admin?${p}`).then((r) => setBoard(r.data)).catch((e) => setErr(formatApiError(e)));
  };
  useEffect(() => { load(); }, [date]);

  if (err) return <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>;
  if (!board) return null;

  return (
    <section className="sharp-card p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-lg font-semibold flex items-center gap-2"><UsersIcon className="h-5 w-5" /> Team board — {fdate(board.date)}</h2>
        <div className="flex flex-wrap items-center gap-2">
          <input data-testid="attendance-export-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)}
            className="rounded-md border border-input bg-background px-2 py-1.5 text-xs" />
          <button data-testid="attendance-export-btn" onClick={exportMonth} disabled={exporting}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
            <FileSpreadsheet className="h-3.5 w-3.5" /> {exporting ? "Exporting…" : "Export month (Excel)"}
          </button>
          <Link to="/app/users" data-testid="attendance-manage-users" className="rounded-md border border-border/70 px-3 py-1.5 text-xs font-semibold hover:bg-secondary">
            Add / remove employees
          </Link>
        </div>
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input data-testid="attendance-date-filter" type="date" value={date} onChange={(e) => setDate(e.target.value)}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm" />
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="attendance-name-filter" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Filter by name or email…" className="w-64 rounded-md border border-input bg-background py-2 pl-8 pr-3 text-sm" />
        </div>
        <button data-testid="attendance-filter-apply" onClick={load} className="rounded-md border border-border/70 px-3 py-2 text-xs font-semibold hover:bg-secondary">Apply</button>
        <span data-testid="attendance-counts" className="text-xs text-muted-foreground">
          <b className="text-emerald-600">{board.counts.checked_in}</b> checked-in · <b className="text-blue-600">{board.counts.present}</b> present · <b className="text-rose-600">{board.counts.absent}</b> absent
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-secondary/40 text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Employee</th><th className="px-3 py-2">Role</th><th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">First In</th><th className="px-3 py-2">Last Out</th><th className="px-3 py-2">Hours</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/40">
            {board.rows.map((r) => (
              <tr key={r.user_id} data-testid={`attendance-emp-${r.user_id}`}>
                <td className="px-3 py-2"><div className="font-medium">{r.name}</div><div className="text-[11px] text-muted-foreground">{r.email}</div></td>
                <td className="px-3 py-2 text-xs capitalize">{(r.role || "").replace(/_/g, " ")}</td>
                <td className="px-3 py-2">
                  <span data-testid={`attendance-status-${r.user_id}`} className={`rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${STATUS_STYLE[r.status]}`}>{STATUS_LABEL[r.status]}</span>
                </td>
                <td className="px-3 py-2 text-xs">{ftime(r.first_in)}</td>
                <td className="px-3 py-2 text-xs">{ftime(r.last_out)}</td>
                <td className="px-3 py-2 text-xs font-semibold">{r.hours} h</td>
              </tr>
            ))}
            {board.rows.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-xs text-muted-foreground">No employees match.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
